<template>
    <q-page padding>
        <div class="q-display-1 q-mb-md text-secondary">
            <q-icon name="far fa-calendar-alt" class="q-mb-sm" /> {{ $t('calendar') }}
        </div>

        <div class="row gutter-sm q-mb-lg">
            <div class="col-xs-12 col-sm-12">
                <q-input v-model="title" ref="title" :float-label="$t('title')" :error="$v.title.$error" @input="$v.title.$touch" :disable="disableFields" />
            </div>
        </div>

        <div class="row gutter-sm q-mb-lg">
            <div class="col-xs-12 col-sm-6">
                <q-datetime v-model="date_start" ref="date_start" type="datetime" :float-label="$t('date_start')" format="DD/MM/YYYY HH:mm:ss" :error="$v.date_start.$error" @input="$v.date_start.$touch" />
            </div>

            <div class="col-xs-12 col-sm-6">
                <q-datetime v-model="date_end" ref="date_end" type="datetime" :float-label="$t('date_end')" format="DD/MM/YYYY HH:mm:ss"  :error="$v.date_end.$error" @input="$v.date_end.$touch"/>
            </div>

            <div class="col-12">
                <q-select v-model="timezone" ref="timezone" :options="timezones" :float-label="$t('timezone')" filter :error="$v.timezone.$error" @input="$v.timezone.$touch" />
            </div>
        </div>

        <q-tabs v-model="tab" color="secondary" class="shadow-1">
            <q-tab slot="title" name="permissions" icon="fas fa-clipboard-check" :label="$t('permissions')" />

            <q-tab-pane name="permissions" class="q-px-lg q-pb-xl">
                <div class="row gutter-sm">
                    <div class="col-xs-12 col-sm-12">
                        <q-select filter autofocus-filter v-model="area" ref="area" :options="areas" :float-label="$t('areas')" :error="$v.area.$error" @input="$v.area.$touch" multiple chips :disable="disableFields" />
                    </div>

                    <div class="col-xs-12 col-sm-12">
                        <p class="caption">{{ $t('profiles') }}</p>

                        <div class="row gutter-sm">
                            <div class="col-xs-12 col-sm-12">
                                <q-checkbox v-model="all_profiles" :val="true" :label="$t('all')" @input="checkAll()" :disable="disableFields" />
                            </div>

                            <div class="col-xs-12 col-sm-4" v-for="(item, index) in profiles" :key="index" :class="getErrorClass($v.profile.$error)">
                                <q-checkbox keep-color v-model="profile" ref="profile" :val="item.value" :label="item.label" @input="$v.profile.$touch" :color="getErrorColor($v.profile.$error)" :disable="disableFields" />
                            </div>
                        </div>
                    </div>
                </div>

                <div class="row gutter-sm">
                    <div class="col-xs-12 col-sm-12">
                        <q-select v-model="user_group" ref="user_group" :options="user_groups" :float-label="$t('groups')" :error="$v.user_group.$error" @input="$v.user_group.$touch" multiple chips :disable="disableFields" />
                    </div>
                </div>
            </q-tab-pane>
        </q-tabs>


        <q-card class="q-mt-md">
            <q-card-title>
                {{ $t('recipients') }} ({{ users.length }})
            </q-card-title>

            <q-card-separator />

            <q-card-main class="relative-position">
                <q-scroll-area style="width: 100%; height: 300px;">
                    <div class="row gutter-sm q-mt-sm">
                        <div class="col-xs-12 col-sm-4" v-for="(user, index) in users" :key="index">
                            {{ user.full_name }}<br>
                            <span class="text-faded">{{ user.email }}</span><br>
                            <span class="text-faded" v-if="status === 2">{{ $t('opened_in') }}: </span>
                            <span class="text-negative" v-if="!user.opened_date && status === 2">-</span>
                            <span class="text-positive" v-if="user.opened_date && status === 2">{{ fDate(user.opened_date) }}</span>
                        </div>
                    </div>

                    <q-inner-loading :visible="loading_users" style="width: 100%; height: 300px;">
                        <q-spinner-dots :size="100" color="secondary"></q-spinner-dots>
                    </q-inner-loading>
                </q-scroll-area>
            </q-card-main>
        </q-card>

        <div class="row gutter-sm justify-end q-mt-lg">
            <div class="col-xs-12 col-sm-3" :class="getOrder('cancel')">
                <q-btn color="tertiary" class="full-width" :label="$t('back')" icon="fas fa-chevron-left" @click="$router.push({ name: 'schedules' })" />
            </div>

            <div class="col-xs-12 col-sm-3" :class="getOrder('save')">
                <q-btn color="secondary" class="full-width" :label="$t('save')" icon="far fa-save" @click="saveData()" :disable="disableFields" />
            </div>
        </div>

    </q-page>
</template>

<script>
import { mapGetters } from 'vuex'
import { required, requiredIf } from 'vuelidate/lib/validators'
import { date } from 'quasar'
const { formatDate, buildDate } = date
import * as moment from 'moment'
import * as tz from 'moment-timezone'

export default {
    i18nOptions: { namespaces: [ 'custom', 'common', 'messages', 'admin'] },

    data() {
        return {
            id: '',
            title: '',
            date_start:'',
            date_end: '',
            status:  1,
            send_date: null,
            area: [],
            profile: [],
            user_group: [],
            all_profiles: false,
            tab: 'permissions',
            users: [],
            area_loaded: false,
            user_group_loaded: false,
            profile_loaded: false,
            loading_users: false,
            show_html: false,
            timezone: ''
        }
    },

    computed: {
        ...mapGetters({
            access_token: 'common/access_token',
            faq_categories: 'registration_lists/faq_categories',
            areas: 'registration_lists/country_areas',
            user_groups: 'registration_lists/group_users',
            profiles: 'registration_lists/profiles',
            id_user_language_selected: 'common/id_user_language_selected',
            timezones: 'registration_lists/timezones'
        }),

        getHeader() {
            let headers = {}
            return headers = {
                headers: {
                    'content-type': 'application/json',
                    'authorization': 'Basic ' + btoa(process.env.APP_CLIENT_ID + ':' + process.env.APP_CLIENT_SECRET),
                    'authorization': 'Bearer ' + this.access_token
                }
            }
        },

        disableFields() {
            if (this.status === 2 || this.status === 3) {
                return true
            }
            else return false
        },

        getMaxWidth() {
            if (this.$q.platform.is.mobile) return '100vw'
            else return '80vw'
        },

        getMinWidth() {
            if (!this.$q.platform.is.mobile) return '90vw'
        }
    },

    methods: {
        saveData() {
            this.$v.$touch()

            if (!this.$v.$error) {
                this.$q.loading.show()

                let data = {
                    title: this.title,
                    date_start: date.formatDate(this.date_start, 'YYYY-MM-DDTHH:mm:ss.SSSZ'),
                    date_end: date.formatDate(this.date_end, 'YYYY-MM-DDTHH:mm:ss.SSSZ'),
                    timezone: this.timezone
                }

                if (this.area.length > 0) data.areas = this.area
                if (this.profile.length > 0) data.profiles = this.profile
                if (this.user_group.length > 0) data.user_groups = this.user_group


                if (this.id != 0) {
                    this.$axios.put(`/admin_calendar/calendar/${this.id}/`, data, this.getHeader)
                    .then(response => {
                        this.$q.loading.hide()

                        this.$q.notify({
                            message: this.$t('message_edit_success'),
                            type: 'positive',
                            icon: 'far fa-save'
                        })

                        this.$router.push({ name: 'schedules' })
                    })
                    .catch(e => {
                        this.$q.loading.hide()
                        console.log(e)
                    })
                }
                else {
                    this.$axios.post(`/admin_calendar/calendar/`, data, this.getHeader)
                    .then(response => {
                        this.$q.loading.hide()

                        this.$q.notify({
                            message: this.$t('message_add_success'),
                            type: 'positive',
                            icon: 'far fa-save'
                        })

                        this.$router.push({ name: 'schedules' })
                    })
                    .catch(e => {
                        this.$q.loading.hide()
                        console.log(e)
                    })
                }
            }
            else {
                this.$formInputErrorFocus(this.$v, this.$refs)
                this.$q.notify({
                    message: this.$t('make_sure_that_all_fields_are_filled_in'),
                    type: 'negative',
                    icon: 'fas fa-exclamation-triangle',
                    position: 'top'
                })
            }
        },

        fetchData() {
            this.$q.loading.show()

            this.$axios.get(`/admin_calendar/calendar/${this.id}/`, this.getHeader)
            .then(response => {
                this.title = response.data.title
                this.date_start = response.data.date_start
                this.date_end = response.data.date_end
                this.profile = response.data.profiles
                this.area = response.data.areas
                this.user_group = response.data.user_groups
                this.timezone = response.data.timezone

                this.getUserList()

                this.$q.loading.hide()
            })
            .catch(e => {
                this.$q.loading.hide()
                console.log(e)
            })
        },

        getOrder(name) {
            if (name == 'cancel') {
                if (this.$q.platform.is.mobile) return 'order-last'
                else return 'order-first'
            }
            else if (name == 'save') {
                if (this.$q.platform.is.mobile) return 'order-first'
                else return 'order-last'
            }
        },

        getErrorColor(state) {
            if (state) return 'negative'
            else return ''
        },

        getErrorClass(state) {
            if (state) return 'checkbox-error'
            else return ''
        },

        getUserList() {
            let fd = new FormData()


            this.area.forEach(area => {
                fd.append('areas', area)
            })

            this.profile.forEach(profile => {
                fd.append('profiles', profile)
            })

            this.user_group.forEach(user_group => {
                fd.append('user_groups', user_group)
            })

            if (this.user_group.length > 0 || (this.area.length > 0 && this.profile.length > 0 )) {
                this.loading_users = true

                if (this.status === 2) {
                    this.$axios.get(`/admin_messages/messages/${this.id}/get_sent_users/`, this.getHeader)
                    .then(response => {
                        let users = []

                        response.data.forEach(user => {
                            users.push(user)
                        })

                        this.users = users
                        this.loading_users = false
                    })
                    .catch(e => {
                        this.loading_users = false
                        console.log(e)
                    })
                }
                else {
                    this.$axios.post(`/admin_messages/messages/get_users/`, fd, this.getHeader)
                    .then(response => {
                        let users = []

                        response.data.forEach(user => {
                            users.push(user)
                        })

                        this.users = users
                        this.loading_users = false
                    })
                    .catch(e => {
                        this.loading_users = false
                        console.log(e)
                    })
                }

            }
            else {
                this.loading_users = false
                this.users = []
            }
        },

        checkAll() {
            if (this.all_profiles) {
                this.profiles.forEach(element => {
                    this.profile.push(element.value)
                })
            }
            else {
                this.profile = []
            }
        },

        fDate(d) {
            if (d) {
                let format = ''

                switch(this.id_user_language_selected) {
                    case 1:
                    case 3: format = 'DD/MM/YYYY'
                    break
                    default: format = 'MM-DD-YYYY'
                    break
                }

                return date.formatDate(d, format)
            }
        },

        fetchTimezones() {
            let values = []

            moment.tz.names()
            .reduce((memo, tz) => {
                memo.push({
                    name: tz,
                    offset: moment.tz(tz).utcOffset()
                })

                return memo
            }, [])
            .sort((a, b) => {
                return a.offset - b.offset
            })
            .reduce((memo, tz) => {
                const timezone = tz.offset ? moment.tz(tz.name).format('Z') : ''
                values.push({ value: tz.name, label: `(GMT${timezone}) ${tz.name}`, ...tz })
            }, '')

            this.$store.dispatch('registration_lists/SET_TIMEZONES', [values])
        }
    },

    watch: {
        area: {
            handler: function (val, oldVal) {
                if (this.area_loaded) {
                    this.getUserList()
                }
                else {
                    this.area_loaded = true
                }
            },
            deep: true
        },
        profile: {
            handler: function (val, oldVal) {
                if (this.profile_loaded) {
                    this.getUserList()
                }
                else {
                    this.profile_loaded = true
                }
            },
            deep: true
        },
        user_group: {
            handler: function (val, oldVal) {
                if (this.user_group_loaded) {
                    this.getUserList()
                }
                else {
                    this.user_group_loaded = true
                }
            },
            deep: true
        }
    },

    created() {
        this.fetchTimezones()
        this.id = this.$route.params.id

        if (this.id != 0) this.fetchData()
        else {
            this.timezone = moment.tz.guess()
            this.area_loaded = true
            this.user_group_loaded = true
            this.profile_loaded = true
        }
    },

    validations() {
        return {
            title: { required },
            date_start: { required },
            date_end: { required },
            timezone: { required },
            area: {
                requiredIf: requiredIf(() => {
                    if(this.profile.length == 0 && this.area.length == 0) {
                        if(this.profile.length == 0 && this.area.length == 0 && this.user_group.length == 0) return true
                        else return false
                    }
                    else {
                        return true
                    }
                })
            },
            profile: {
                requiredIf: requiredIf(() => {
                    if(this.profile.length == 0 && this.area.length == 0) {
                        if(this.profile.length == 0 && this.area.length == 0 && this.user_group.length == 0) return true
                        else return false
                    }
                    else {
                        return true
                    }
                })
            },
            user_group: {
                requiredIf: requiredIf(() => {
                    if(this.profile.length == 0 && this.area.length == 0 && this.user_group.length == 0) {
                        return true
                    }
                    else {
                        return false
                    }
                })
            }
        }
    }
}
</script>

<style>

</style>
